/**
 * truncation_controller.js — mobile "show more" row truncation (Stimulus).
 *
 * Ports static/js/dashboard-truncation.js to an element-scoped controller, one
 * instance per [data-truncate-rows] section (data-controller="truncation"). On
 * mobile a section shows ROWS_PER_PAGE rows with a "Show more" button revealing
 * the rest incrementally; on desktop all rows show.
 *
 * Lifecycle: connect() applies truncation to this.element; disconnect() and a
 * connect-scoped turbo:before-cache fully REVERT the DOM (remove the injected
 * show-more button, strip row-hidden/row-visible/row-appearing classes + inline
 * styles) so the cached snapshot matches the initial server render. Leaving
 * those mutations in the snapshot was the original bug. The instance owns its
 * visibleCount state (was a shared sectionStates map). list-search:updated
 * refreshes the owned row set after the controller swaps rows in; while
 * data-search-active is 'true' on the section, truncation stays off so every
 * matching row remains visible.
 */
import { Controller } from "@hotwired/stimulus";

const MOBILE_BREAKPOINT = 768;
const SCROLL_OFFSET = 16;


function scrollToSection(sectionCard) {
    if (!sectionCard) return;
    requestAnimationFrame(function () {
        var rect = sectionCard.getBoundingClientRect();
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var targetY = rect.top + scrollTop - SCROLL_OFFSET;
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    });
}

export default class extends Controller {
    connect() {
        this.perPage = parseInt(this.element.dataset.truncateRows, 10) || 3;
        this.lastWidth = window.innerWidth;
        this._apply();

        this._onResize = () => {
            var newWidth = window.innerWidth;
            if (newWidth === this.lastWidth) return;  // ignore height-only changes (mobile scroll)
            this.lastWidth = newWidth;
            clearTimeout(this._resizeTimer);
            this._resizeTimer = setTimeout(() => this._apply(), 150);
        };
        window.addEventListener('resize', this._onResize);

        this._onBeforeCache = () => this._revert();
        document.addEventListener('turbo:before-cache', this._onBeforeCache);

        this._onRowsUpdated = () => this.refresh();
        this.element.addEventListener('list-search:updated', this._onRowsUpdated);
    }

    disconnect() {
        if (this._onResize) {
            window.removeEventListener('resize', this._onResize);
            this._onResize = null;
        }
        clearTimeout(this._resizeTimer);
        if (this._onBeforeCache) {
            document.removeEventListener('turbo:before-cache', this._onBeforeCache);
            this._onBeforeCache = null;
        }
        if (this._onRowsUpdated) {
            this.element.removeEventListener('list-search:updated', this._onRowsUpdated);
            this._onRowsUpdated = null;
        }
        this._revert();
    }

    refresh() {
        this._revert();
        if (this.element.dataset.searchActive !== 'true') this._apply();
    }

    _apply() {
        var section = this.element;
        var tbody = section.querySelector('tbody');
        if (!tbody) return;

        var rows = Array.from(tbody.querySelectorAll('tr:not(.empty-state-row)'));
        var totalRows = rows.length;
        if (totalRows <= this.perPage) return;

        var sectionCard = section.closest('.section-card');

        rows.forEach(function (row) {
            if (!row.classList.contains('truncate-row')) {
                row.classList.add('truncate-row');
            }
        });

        if (this.visibleCount == null) {
            this.visibleCount = this.perPage;
        }

        var btn = section.querySelector('.show-more-btn');
        if (!btn) {
            btn = document.createElement('button');
            btn.className = 'btn btn-link btn-sm w-100 show-more-btn';
            section.appendChild(btn);
        }
        this._btn = btn;

        btn.onclick = () => {
            var wasExpanded = this.visibleCount >= totalRows;
            var previousCount = this.visibleCount;
            if (wasExpanded) {
                this.visibleCount = this.perPage;
                this._updateVisibility(rows, totalRows, btn, false);
                setTimeout(function () { scrollToSection(sectionCard); }, 50);
            } else {
                this.visibleCount = Math.min(this.visibleCount + this.perPage, totalRows);
                this._updateVisibility(rows, totalRows, btn, true, previousCount);
            }
        };

        this._updateVisibility(rows, totalRows, btn, false);
    }

    // Mobile is judged from the width cached at connect and on resize, never a
    // fresh window.innerWidth: this runs right after list-search swaps in every
    // dashboard row, and each innerWidth read there forces a full style and
    // layout pass over the whole list (400ms on a slow phone at 20 rows each).
    _isMobile() {
        return this.lastWidth < MOBILE_BREAKPOINT;
    }

    _updateVisibility(rows, totalRows, btn, animate, previousCount) {
        var self = this;
        var mobile = this._isMobile();
        rows.forEach(function (row, i) {
            var shouldBeVisible = !mobile || i < self.visibleCount;
            var wasHidden = row.classList.contains('row-hidden');
            var wasShown = row.classList.contains('row-visible');

            row.classList.remove('row-appearing');

            if (shouldBeVisible) {
                row.classList.remove('row-hidden');
                row.classList.add('row-visible');
                row.style.display = '';

                if (animate && wasHidden && i >= previousCount) {
                    row.style.animationDelay = ((i - previousCount) * 50) + 'ms';
                    row.classList.add('row-appearing');
                }
            } else if (!wasShown) {
                // Never on screen, so there is no fade to let finish. Hiding it
                // now keeps it out of layout altogether: row-hidden alone is
                // position:absolute, which is still laid out.
                row.classList.add('row-hidden');
                row.style.display = 'none';
            } else {
                row.classList.remove('row-visible');
                row.classList.add('row-hidden');
                setTimeout(function () {
                    if (row.classList.contains('row-hidden')) {
                        row.style.display = 'none';
                    }
                }, 250);
            }
        });

        if (!mobile) {
            btn.style.display = 'none';
            return;
        }
        btn.style.display = '';
        var remaining = totalRows - this.visibleCount;
        if (this.visibleCount >= totalRows) {
            btn.innerHTML = '<i class="bi bi-chevron-up me-1"></i>Show less';
        } else {
            btn.innerHTML = '<i class="bi bi-chevron-down me-1"></i>Show ' + Math.min(this.perPage, remaining) + ' more';
        }
    }

    // Restore the section to its server-rendered state for a clean snapshot.
    _revert() {
        var section = this.element;
        if (this._btn) {
            this._btn.remove();
            this._btn = null;
        } else {
            var btn = section.querySelector('.show-more-btn');
            if (btn) btn.remove();
        }
        section.querySelectorAll('.truncate-row').forEach(function (row) {
            row.classList.remove('row-hidden', 'row-visible', 'row-appearing');
            row.style.display = '';
            row.style.animationDelay = '';
        });
        this.visibleCount = null;
    }
}
